import React, { useEffect, Suspense } from 'react'
import './Subscriptions.css'
import SubscriptionRow from './SubscriptionRow'
import { Subscription, SubscriptionState } from '../store/store'
import { FiltersState } from '../store/store'
import { useLocation, useHistory } from 'react-router-dom'
import SubscriptionsFilterBar from './SubscriptionsFilterBar'
import MessageBox from './MessageBox'
import SubscriptionRowCompact from './SubscriptionRowCompact'
import { RequestState } from '../../types'
import { useTranslation } from 'react-i18next'
import Content from '../../../components/layout/Content'
import ContentBody from '../../../components/layout/ContentBody'
import SubscriptionsFilterPanel from './SubscriptionsFilterPanel'
import SuspenseFallback from '../../../components/layout/SuspenseFallback'
import ReactGA from 'react-ga'
import EmptyList from '../../../components/EmptyList'

type Props = {
  state: SubscriptionState
  filters: FiltersState
  partnerId: string
  loadFilters: (partnerId: string) => Promise<void>
  loadSubscriptions: (page: number, partnerId: string, sort: string) => Promise<void>
  loadFilteredSubscriptions: (filter: string, page: number, partnerId: string, sort: string) => Promise<void>
  loadSubscriptionDetails: (subscriptionId: string) => Promise<void>
  loadSingleSubscription: (subscriptionId: string) => Promise<void>
  setFilterPanelOpen: (open: boolean) => void
  setSubscriptionExpanded: (subscriptionId: string, expanded: boolean) => void
  loadAccountSubscription: (accountId: string) => Promise<void>
  searchSubscriptions: (partnerId: string, text: string, includeDeactivated: boolean, page: number, sort: string) => Promise<void>
  loadSubscriptionAccountFilters: (subscriptionId: string, accountId: string) => Promise<void>
}

function Subscriptions(props: Props) {
  const {
    state,
    filters,
    partnerId,
    loadSubscriptions,
    loadFilters,
    loadFilteredSubscriptions,
    loadSubscriptionDetails,
    setFilterPanelOpen,
    loadSingleSubscription,
    loadAccountSubscription,
    setSubscriptionExpanded,
    searchSubscriptions,
    loadSubscriptionAccountFilters
  } = props

  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation(['subscriptions', 'global'])

  const {
    subscriptions,
    loadedPages,
    totalPages,
    totalCount,
    filterPanelOpen,
    searchedForAccount,
    requestState
  } = state

  const params = new URLSearchParams(location.search)
  const filter = params.get('filter')
  const subscriptionId = params.get('subscription')
  const accountId = params.get('account')
  const subscriptionSearch = params.get('search')
  const includeDeactivatedParam = params.get('deactivated')
  const includeDeactivated = params.get('deactivated') === "true" || false
  const sort = params.get('sort') ? params.get('sort') as string : 'name'
  const singleItem = subscriptionId || accountId

  useEffect(
    () => {
      if (partnerId) {
        loadFilters(partnerId)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partnerId]
  )

  useEffect(() => {
    if (partnerId) {
      if (subscriptionId) {
        loadSingleSubscription(subscriptionId)
      } else if (accountId) {
        loadAccountSubscription(accountId)
      } else if (subscriptionSearch) {
        searchSubscriptions(partnerId, subscriptionSearch, includeDeactivated, 1, sort)
        ReactGA.event({ category: 'subscriptions', action: 'Search' })
      } else if (filter) {
        const foundFilter = filters.filters.find(f => f.id === filter)

        if (foundFilter) {
          loadFilteredSubscriptions(filter, 1, partnerId, sort)
          ReactGA.event({ category: 'subscriptions', action: 'Filter', label: foundFilter.key })
        } else {
          loadSubscriptions(1, partnerId, sort)
        }
      } else {
        loadSubscriptions(1, partnerId, sort)
      }
    }
  },
    // eslint-disable-next-line
    [partnerId, filter, subscriptionId, accountId, subscriptionSearch, includeDeactivated, sort])

  function loadNextPage() {
    if (partnerId) {
      if (subscriptionSearch) {
        searchSubscriptions(partnerId, subscriptionSearch, includeDeactivated, loadedPages + 1, sort)
      } else if (filter) {
        loadFilteredSubscriptions(filter, loadedPages + 1, partnerId, sort)
      } else {
        loadSubscriptions(loadedPages + 1, partnerId, sort)
      }
    }
  }

  function filteringSupported() {
    if (subscriptionId || accountId || subscriptionSearch) {
      return false
    } else {
      return true
    }
  }

  function sortArrow(field: string) {
    if (accountId !== null || subscriptionId !== null) {
      return null
    } else {
      if (sort === field) {
        return (
          <i className="fas fa-arrow-down sorting-arrow"></i>
        )
      } else if (sort === `-${field}`) {
        return (
          <i className="fas fa-arrow-up sorting-arrow"></i>
        )
      } else {
        return (
          <i className="fas fa-arrow-down sorting-arrow-transparent"></i>
        )
      }
    }
  }

  function renderLoadMore() {
    let showLoadMore = null

    if (subscriptionId || accountId) {
      showLoadMore = false
    } else {
      showLoadMore = true
    }

    if (
      showLoadMore &&
      totalPages &&
      loadedPages > 0 &&
      loadedPages < totalPages &&
      requestState !== RequestState.Error
    ) {
      return (
        <div className="flex-center">
          <button id="load-more" disabled={requestState === RequestState.Loading} onClick={loadNextPage}>
            {requestState !== RequestState.Loading ? t('global:load_more') : t('global:loading')}
          </button>
        </div>
      )
    }
  }

  function renderMessageBox() {
    if (requestState === RequestState.Loading) {
      return (
        <MessageBox>
          {t('global:please_wait')}
        </MessageBox>
      )
    } else if (accountId) {
      let accountName

      if (searchedForAccount) {
        accountName = searchedForAccount.name
      }

      return (
        <MessageBox>
          <span>{t('searched_for_account')} <b>{accountName}</b></span>
        </MessageBox>
      )
    } else if (subscriptionId) {
      let subscriptionName

      if (subscriptions && subscriptions.length > 0) {
        subscriptionName = subscriptions[0].name
      }

      return (
        <MessageBox>
          <span>{t('searched_for_subscription')} <b>{subscriptionName}</b></span>
        </MessageBox>
      )
    } else if (subscriptionSearch) {
      return (
        <MessageBox>
          <span>{t('searched_for')} <b>{subscriptionSearch}</b></span>
          {totalCount > 0 && (
            <span>&nbsp;({totalCount})</span>
          )}
        </MessageBox>
      )
    }
  }

  function onSortClick(field: string) {
    if (accountId === null && subscriptionId === null) {
      const filterQuery = filter ? `&filter=${filter}` : ''
      const accountQuery = accountId ? `&account=${accountId}` : ''
      const searchQuery = subscriptionSearch ? `&search=${subscriptionSearch}` : ''
      const includeDeactivatedQuery = includeDeactivatedParam ? `&deactivated=${includeDeactivatedParam}` : ''
      const queries = `${filterQuery}${accountQuery}${searchQuery}${includeDeactivatedQuery}`

      if (sort === null && field === 'name') {
        history.push(`/partner/${partnerId}/subscriptions?sort=-name${queries}`)
      } else {
        if (sort === field) {
          history.push(`/partner/${partnerId}/subscriptions?sort=-${field}${queries}`)
        } else {
          history.push(`/partner/${partnerId}/subscriptions?sort=${field}${queries}`)
        }
      }
    }
  }

  function getSortableHeaderClass() {
    if (accountId === null && subscriptionId === null) {
      return 'cell-header-sortable'
    } else {
      return ''
    }
  }

  const filtering = filteringSupported()

  return (
    <Suspense fallback={<SuspenseFallback />}>
      {filtering && (
        <SubscriptionsFilterBar
          subscriptionCount={totalCount}
          filters={filters}
          filterPanelOpen={filterPanelOpen}
          setFilterPanelOpen={setFilterPanelOpen}
        />
      )}

      {!filtering && (
        renderMessageBox()
      )}

      <ContentBody>
        {filterPanelOpen && (
          <SubscriptionsFilterPanel currentFilterId={filter} filters={filters} />
        )}

        <Content>
          {requestState === RequestState.Error && (
            <div className="padding-m">{t('error')}</div>
          )}

          {requestState === RequestState.Loading && (loadedPages === 0 || singleItem) && (
            <div className="panel-loading">
              <i className="fas fa-spinner fa-spin font-size-xl"></i>
              <span>{t('global:loading')}</span>
            </div>
          )}

          {requestState !== RequestState.Error && subscriptions && subscriptions.length > 0 && (
            <table id="subscriptions" className="content-table" cellSpacing={0} cellPadding={5}>
              <thead>
                <tr>
                  <th />
                  <th data-cy="sort-name" className={getSortableHeaderClass()} onClick={() => onSortClick('name')}>
                    {t('fields.subscription')}{sortArrow('name')}
                  </th>
                  <th data-cy="sort-package" className={getSortableHeaderClass()} onClick={() => onSortClick('package_name')}>
                    {t('fields.package')}{sortArrow('package_name')}
                  </th>
                  <th data-cy="sort-records">
                    {t('fields.records')}
                  </th>
                  <th data-cy="sort-mebers">
                    {t('fields.members')}
                  </th>
                  <th data-cy="sort-renewal" className={getSortableHeaderClass()} onClick={() => onSortClick('renewal_date')}>
                    {t('fields.renewal')}{sortArrow('renewal_date')}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription: Subscription) => (
                  <SubscriptionRow key={subscription.id}
                    subscription={subscription}
                    loadSubscriptionDetails={loadSubscriptionDetails}
                    setSubscriptionExpanded={setSubscriptionExpanded}
                    loadSubscriptionAccountFilters={loadSubscriptionAccountFilters}
                  />
                ))}
              </tbody>
            </table>
          )}

          <div id="responsive-subscriptions-container">
            {subscriptions.map((subscription: Subscription) => (
              <Suspense fallback={<SuspenseFallback />} key={subscription.id}>
                <SubscriptionRowCompact
                  subscription={subscription}
                  loadSubscriptionDetails={loadSubscriptionDetails}
                  setSubscriptionExpanded={setSubscriptionExpanded}
                  loadSubscriptionAccountFilters={loadSubscriptionAccountFilters}
                />
              </Suspense>
            ))}
          </div>

          {requestState === RequestState.Loaded && subscriptions && subscriptions.length === 0 && (
            <EmptyList icon="far fa-clone" message={t('no_subscriptions')} />
          )}

          {renderLoadMore()}
        </Content>
      </ContentBody>
    </Suspense>
  )
}

export default Subscriptions
